import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import PnlHeaderCard from '../components/agreementDetail/PnlHeaderCard';
import Worksheet from '../components/proposalGrid/Worksheet';
import { pnlApi } from '../utils/api';
import { MEASURE_CAP_WIDE, MEASURE_CAP_NARROW, MEASURE_FLOOR_WIDE, MEASURE_FLOOR_NARROW } from '../components/proposalGrid/helpers';

// PRD-024 / PRD-014 — the single-pane P&L Agreement Detail surface. Every number
// comes from GET /api/agreements/:id/pnl (server-computed). The signed Agreement
// renders on the SAME spreadsheet worksheet grid as the Proposal Editor
// (mode='agreement'), read-only, with the derived hero strip + per-Outcome Sell
// Config (price × qty = revenue; no allocation %) and Estimated→Actual cost.

function railStepFor(pnl) {
  const os = pnl.outcomes || [];
  // A signed Agreement is in the "verifying" (delivery) phase until every
  // Outcome settles — matches the mockup rail (Drafting · Signed both past).
  if (os.length > 0 && os.every((o) => o.status === 'settled')) return 'settled';
  return 'verifying';
}

// Map the canonical /pnl outcomes onto the worksheet row shape. Cost/margin
// fields are present only when cost_side_visible (buyer-share gate, Principle #6).
function toWorksheetRows(pnl) {
  return (pnl.outcomes || []).map((o) => ({
    buyerSignoff: o.buyer_signoff,
    refuteNote: o.refute_note,
    outcomeId: o.outcome_id,
    name: o.name,
    status: o.status,
    // PRD-024 — per-Outcome Sell Config (revenue = price × qty; no allocation).
    revenueCents: o.revenue_cents != null ? o.revenue_cents : o.allocation_cents,
    sellUnitName: o.sell_unit_name || '',
    sellUnitNoun: o.sell_unit_noun || '',
    // PRD-027 WU-G — frozen active (delivery) window.
    activeFrom: o.active_from || null,
    activeTo: o.active_to || null,
    sellPricePerUnitCents: o.sell_price_per_unit_cents != null ? o.sell_price_per_unit_cents : null,
    sellQuantity: o.sell_quantity != null ? o.sell_quantity : null,
    costCents: o.cost_cents != null ? o.cost_cents : 0,
    costLines: o.cost_lines || [],
    estimatedCostCents: o.estimated_cost_cents != null ? o.estimated_cost_cents : null,
    actualCostCents: o.actual_cost_cents != null ? o.actual_cost_cents : null,
    costVarianceCents: o.cost_variance_cents != null ? o.cost_variance_cents : null,
    marginCents: o.margin_cents != null ? o.margin_cents : 0,
    marginPct: o.margin_pct != null ? o.margin_pct : 0,
    evidence: o.evidence || [],
    deliverables: o.deliverables || [],
    // PRD-030 WU-11 — server-computed time-aware delivery status (F7a).
    delivery: o.delivery || null,
    verifiedAt: o.verified_at,
    verifiedBy: o.verified_by,
    acceptedAt: o.accepted_at,
    needsReview: o.needs_review,
    currency: pnl.currency,
  }));
}

function Skeleton() {
  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-pulse">
      <div className="card p-6">
        <div className="h-5 w-64 bg-gray-200 rounded" />
        <div className="mt-6 grid grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => <div key={i} className="h-12 bg-gray-200 rounded" />)}
        </div>
      </div>
      <div className="card p-6 space-y-3" style={{ borderLeft: '3px solid #2563eb' }}>
        {[0, 1, 2].map((i) => <div key={i} className="h-8 bg-gray-100 rounded" />)}
      </div>
    </div>
  );
}

export default function AgreementDetailV3() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pnl, setPnl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  // Optimistic override for the share toggle (WU-2).
  const [shareOverride, setShareOverride] = useState(null);

  const fetchPnl = useCallback(async () => {
    try {
      const res = await pnlApi.get(id);
      setPnl(res.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) setError('not_found');
      else setError('error');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchPnl(); }, [fetchPnl]);

  // PRD-031 F6.1 — permanent legacy deep-link shim: already-delivered emails
  // carry Agreement-page URLs (?tab=progress|evidence|status); they now mean
  // the Delivery sub-page.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab && ['status', 'progress', 'evidence'].includes(tab)) {
      navigate(`/agreements/${id}/delivery?tab=${tab}`, { replace: true });
    }
  }, [id, navigate]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const onToggleShare = async (shared) => {
    setShareOverride(shared);
    try {
      await pnlApi.setShareUnitEconomics(id, shared);
      await fetchPnl();
      setShareOverride(null);
    } catch (err) {
      setShareOverride(null);
      const status = err.response?.status;
      if (status === 403) showToast('Only the agency can change sharing');
      else showToast("Couldn't update sharing — try again");
    }
  };

  if (loading) return <Layout><Skeleton /></Layout>;
  if (error === 'not_found') return <Layout><div className="max-w-4xl mx-auto card p-8 text-center text-gray-600">Agreement not found. <button className="text-primary-600" onClick={() => navigate('/agreements')}>Back to Agreements</button></div></Layout>;
  if (error || !pnl) return <Layout><div className="max-w-4xl mx-auto card p-8 text-center text-gray-600">Failed to load this Agreement.</div></Layout>;

  const isSeller = pnl.role === 'seller';
  const costVisible = pnl.cost_side_visible;
  const t = pnl.totals;
  const rows = toWorksheetRows(pnl);
  // PRD-031 Cluster E — header delivery summary, derived from the
  // server-computed per-Outcome delivery statuses (WU-11).
  const now = Date.now();
  const lastCheckedAt = rows.reduce((max, r) => {
    const ts = r.delivery?.last_checked_at;
    return ts && (!max || new Date(ts) > new Date(max)) ? ts : max;
  }, null);
  const deliverySummary = {
    outcomeCount: rows.length,
    deliveredCount: rows.filter((r) => (r.delivery?.status || '').startsWith('delivered')).length,
    pendingAcceptanceCount: rows.filter((r) => r.buyerSignoff === 'awaiting_buyer').length,
    lastCheckedAt,
    allCheckedWithin24h: rows.length > 0 && rows.every((r) => {
      const ts = r.delivery?.last_checked_at;
      return ts && now - new Date(ts).getTime() < 24 * 60 * 60 * 1000;
    }),
  };
  // The page (and its worksheet) share one measure cap: 1160px for the
  // 10-track P&L configurations, 840px for the 6-track Client-private one —
  // surplus width goes to the container margin, never into a dead prose gap.
  const measureCap = costVisible ? MEASURE_CAP_WIDE : MEASURE_CAP_NARROW;
  // WU-B — the ratified floor (economics-row-final.html): the card never
  // compresses below it; narrower windows pan horizontally instead of
  // wrapping header words and crushing prose.
  const measureFloor = costVisible ? MEASURE_FLOOR_WIDE : MEASURE_FLOOR_NARROW;
  const signedAt = pnl.timeline?.buyer_signed_at || pnl.timeline?.seller_signed_at || pnl.timeline?.created_at;
  const fmtSigned = signedAt ? new Date(signedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  // PRD-031 Cluster E — the grid's Margin column foots itself (revenue −
  // actual, matching the Realized-margin hero); the expected-margin basis
  // (revenue − GREATEST(est, actual)) no longer renders on this surface,
  // superseding the 2026-07-05 footer-basis display.
  const wsTotals = {
    revenueCents: t.revenue_cents,
    costCents: t.cost_cents != null ? t.cost_cents : 0,
    estimatedCostCents: t.estimated_cost_cents != null ? t.estimated_cost_cents : 0,
  };

  return (
    <Layout>
      {/* PRD-031 Cluster E — one measure cap for the page: surplus width goes
          to the container margin (E4), and the whole surface shares the
          worksheet's axis. */}
      <div className="mx-auto space-y-4" style={{ maxWidth: `${measureCap}px` }}>
        <PnlHeaderCard
          deliveryHref={`/agreements/${id}/delivery`}
          parties={pnl.parties}
          title={pnl.name}
          railStep={railStepFor(pnl)}
          totals={pnl.totals}
          timeline={pnl.timeline}
          delivery={deliverySummary}
          costVisible={costVisible}
          currency={pnl.currency}
          isSeller={isSeller}
          shared={shareOverride != null ? shareOverride : pnl.unit_economics_shared}
          onToggleShare={onToggleShare}
        />

        {/* The unified worksheet — the SAME grid as the Proposal Editor
            (grid v2, one balance sheet at two lifecycle stages). */}
        <Worksheet
          mode="agreement"
          agreementId={id}
          rows={rows}
          showCost={costVisible}
          isSeller={isSeller}
          isBuyer={!isSeller}
          totals={wsTotals}
          caption={{ title: 'Signed Agreement · per-outcome economics', right: fmtSigned ? `Signed ${fmtSigned}` : null }}
          disclosure={costVisible && t.take_rate_cents != null ? {
            takeRatePct: t.take_rate_pct,
            takeRateCents: t.take_rate_cents,
          } : null}
        />


      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50">{toast}</div>
      )}
    </Layout>
  );
}
